import React, { useState } from 'react';
import '../style/report.css';
import { API_GET_ADVANCE_REPAYMENT } from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

const AdvanceRepaymentReport = () => {
  const { logout, selectedBranchId } = useAuth();
  const token = localStorage.getItem('token');

  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    from_date: '',
    to_date: ''
  });
  const [columnFilter, setColumnFilter] = useState({
    branch_no: '',
    branch_name: '',
    center_no: '',
    center_name: '',
    emp_generate_id: '',
    emp_name: '',
    client_no: '',
    client_name: ''
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleColumnFilterChange = (e) => {
    const { name, value } = e.target;
    setColumnFilter(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const getAdvanceRepaymentReport = async () => {
    setErrorMessage('');
    if (formData.from_date && formData.to_date && formData.from_date > formData.to_date) {
      setErrorMessage('From date must be on or before to date.');
      return;
    }
    setIsLoading(true);
    setReportData([]);
    setHasSubmitted(false);
    try {
      const response = await fetch(API_GET_ADVANCE_REPAYMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...formData, ...(selectedBranchId ? { branch_id: selectedBranchId } : {}) }),
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const result = await response.json();
      if (!response.ok || result?.status === false) {
        throw new Error(result?.message || result?.error || 'Unable to load the report.');
      }
      setReportData(result?.data || []);
      setHasSubmitted(true);
      setCurrentPage(1);
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while fetching the report.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = reportData.filter(item =>
    Object.entries(columnFilter).every(([key, val]) =>
      !val || String(item[key] ?? '').toLowerCase().includes(val.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalAmountAll = filteredData.reduce((sum, d) => sum + parseFloat(d.total_future_repayment_amt || 0), 0);
  const totalAmountPage = currentItems.reduce((sum, d) => sum + parseFloat(d.total_future_repayment_amt || 0), 0);
  const totalPrincipalAll = filteredData.reduce((sum, d) => sum + parseFloat(d.total_future_principal_amt || 0), 0);
  const totalPrincipalPage = currentItems.reduce((sum, d) => sum + parseFloat(d.total_future_principal_amt || 0), 0);
  const totalInterestAll = filteredData.reduce((sum, d) => sum + parseFloat(d.total_future_interest_amt || 0), 0);
  const totalInterestPage = currentItems.reduce((sum, d) => sum + parseFloat(d.total_future_interest_amt || 0), 0);
  const totalRemainingAll = filteredData.reduce((sum, d) => sum + parseInt(d.remaining_installments || 0), 0);
  const totalRemainingPage = currentItems.reduce((sum, d) => sum + parseInt(d.remaining_installments || 0), 0);
  const installAmt = currentItems.reduce((sum, d) => sum + parseInt(d.cycle_installment_amt || 0), 0);
  const totalInstallAmt = filteredData.reduce((sum, d) => sum + parseInt(d.cycle_installment_amt || 0), 0);



  const exportToCSV = () => {
    const headers = [
      'Branch No', 'Branch Name', 'Center No', 'Center Name',
      'Emp ID', 'Emp Name', 'Client No', 'Client Name',
      'Installment Amt', 'Remaining Inst.', 'Next Repayment Date', 'Future Principal Amt', 'Future Interest Amt', 'Future Repayment Amt',
    ];
    const csvRows = [
      headers.join(','),
      ...filteredData.map(d => [
        d.branch_no, d.branch_name, d.center_no, d.center_name,
        d.emp_generate_id, d.emp_name, d.client_no, d.client_name,
        d.cycle_installment_amt, d.remaining_installments,
        d.next_repayment_date, d.total_future_principal_amt, d.total_future_interest_amt, d.total_future_repayment_amt,
      ].join(','))
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'AdvanceRepaymentReport.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="report-container">
      <h2>Advance Repayment Report</h2>

      <div className="filter-section">
        <input type="date" name="from_date" value={formData.from_date} onChange={handleFormChange} />
        <input type="date" name="to_date" value={formData.to_date} onChange={handleFormChange} />
        <button onClick={getAdvanceRepaymentReport} disabled={isLoading}>{isLoading ? 'Loading...' : 'Submit'}</button>
        <button onClick={exportToCSV}>Export to CSV</button>
      </div>

      {errorMessage && <div role="alert">{errorMessage}</div>}
      {hasSubmitted && !filteredData.length && <p>No advance repayments found for the selected filters.</p>}

      <div className="table-responsive data-table-shell">
        <table className="advance-repayment-table data-table">
          <thead>
            <tr>
              <th>Branch No</th>
              <th>Branch Name</th>
              <th>Center No</th>
              <th>Center Name</th>
              <th>Emp ID</th>
              <th>Emp Name</th>
              <th>Client No</th>
              <th>Client Name</th>
              <th>Installment Amt</th>
              <th>Remaining Inst.</th>
              <th>Next Repayment Date</th>
              <th>Future Principal Amt</th>
              <th>Future Interest Amt</th>
              <th>Future Repayment Amt</th>
            </tr>
            <tr>
              {['branch_no', 'branch_name', 'center_no', 'center_name', 'emp_generate_id', 'emp_name', 'client_no', 'client_name','cycle_installment_amt','remaining_installments','next_repayment_date'].filter(field => field !== null).map(field => (
                <th key={field}>
                  <input
                    type="text"
                    placeholder={`Filter ${field.replace(/_/g, ' ')}`}
                    name={field}
                    value={columnFilter[field] || ''}
                    onChange={handleColumnFilterChange}
                    className="column-filter-input"
                  />
                </th>
              ))}
              <th colSpan="3"></th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((d, i) => (
              <tr key={i}>
                <td>{d.branch_no}</td>
                <td>{d.branch_name}</td>
                <td>{d.center_no}</td>
                <td>{d.center_name}</td>
                <td>{d.emp_generate_id}</td>
                <td>{d.emp_name}</td>
                <td>{d.client_no}</td>
                <td>{d.client_name}</td>
                <td>{d.cycle_installment_amt}</td>
                <td>{d.remaining_installments}</td>
                <td>{d.next_repayment_date}</td>
                <td>{Number(d.total_future_principal_amt || 0).toFixed(2)}</td>
                <td>{Number(d.total_future_interest_amt || 0).toFixed(2)}</td>
                <td>{d.total_future_repayment_amt}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="8"><strong>Page Total:</strong></td>
              <td>{installAmt}</td>
              <td><strong>{totalRemainingPage}</strong></td>
              <td></td>
              <td><strong>{totalPrincipalPage.toFixed(2)}</strong></td>
              <td><strong>{totalInterestPage.toFixed(2)}</strong></td>
              <td><strong>{totalAmountPage.toFixed(2)}</strong></td>
            </tr>
            <tr>
              <td colSpan="8"><strong>Grand Total:</strong></td>
              <td>{totalInstallAmt}</td>
              <td>{totalRemainingAll}</td>
              <td></td>
              <td><strong>{totalPrincipalAll.toFixed(2)}</strong></td>
              <td><strong>{totalInterestAll.toFixed(2)}</strong></td>
              <td><strong>{totalAmountAll.toFixed(2)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="pagination-controls">
        <span>Items per page: </span>
        <div style={{float:'left',width:'70%'}}>
        <select value={itemsPerPage} onChange={e => {
          setItemsPerPage(Number(e.target.value));
          setCurrentPage(1);
        }} style={{ width: '60px' }}>
          {ITEMS_PER_PAGE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>  
        {totalPages > 1 && (
          <div className="pagination" style={{  float: 'right' }}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={currentPage === i + 1 ? 'active' : ''}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceRepaymentReport;
